// The window-1 findings, read once per Encaissement screen and shared (L0b).
//
// The desk's sheets are keyed by arrival or departure date; findings belong to a
// PAYMENT day. So the sheets do not each fetch: the Encaissement screen reads
// the findings of the day just ended (the one Vega has not closed yet) and
// provides them here, and a row asks `<FindingFlag>` whether its booking is in
// them.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CardFinding, CardFindingsDay, IsoDate } from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms, usePmsReachOrEmpty, usePmsScope } from './PmsProvider.js';
import { findingLine, findingsFor } from './cardFindingsView.js';

const REFRESH_MS = 60_000;

export interface CardFindingsState {
  day: CardFindingsDay | null;
  /** The read failed: the screen must say so, never fall back to « nothing found ». */
  error: string | null;
  loading: boolean;
}

const Ctx = createContext<readonly CardFinding[]>([]);

export function CardFindingsFlags({ findings, children }: { findings: readonly CardFinding[]; children: ReactNode }) {
  return <Ctx.Provider value={findings}>{children}</Ctx.Provider>;
}

/** Its own row under a booking whose card payment has an open finding: plain
 *  text, one line per finding, full width. Renders nothing otherwise, so it can
 *  sit after every row of a sheet. Operator 2026-10-04: a small flag beside the
 *  name was too easy to miss. */
export function FindingRow({
  clientRef,
  bookingRef,
  colSpan,
}: {
  clientRef: string | null;
  bookingRef: string | null;
  colSpan: number;
}) {
  const findings = useContext(Ctx);
  const hits = useMemo(() => findingsFor(findings, clientRef, bookingRef), [findings, clientRef, bookingRef]);
  if (hits.length === 0) return null;
  return (
    <tr className="pms-finding-row">
      <td colSpan={colSpan}>
        {hits.map((f, i) => (
          <p key={i}>
            <strong>⚠ Contrôle carte</strong> — {findingLine(f)}
          </p>
        ))}
      </td>
    </tr>
  );
}

/** One day's findings, re-read every minute while mounted. Reads nothing
 *  without `fin.findings.read`. */
export function useCardFindingsDay(day: IsoDate): CardFindingsState & { allowed: boolean } {
  const { cardFindings } = usePms();
  const { current } = usePmsScope();
  const allowed = useCapability(CAPABILITIES.FIN_FINDINGS_READ);
  const [state, setState] = useState<CardFindingsState>({ day: null, error: null, loading: false });

  useEffect(() => {
    if (!current || !allowed) return;
    let cancelled = false;
    const load = async () => {
      const res = await cardFindings.day(current, day);
      if (cancelled) return;
      setState(
        res.ok
          ? { day: res.value, error: null, loading: false }
          : { day: null, error: res.error.message ?? 'lecture impossible', loading: false },
      );
    };
    setState((s) => ({ ...s, loading: true }));
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [cardFindings, current, allowed, day]);

  return { ...state, allowed };
}

/** The findings of EVERY établissement in reach, merged (U3) — for the flags on
 *  the unified sheets and the « Contrôles » chip's count. A flag belongs to a
 *  row, and the sheets list every building's rows: reading only one building's
 *  findings would leave the other's rows silently unflagged. With one
 *  établissement, its findings — what the screen showed before. The per-day
 *  view (« Contrôles ») stays one établissement's: `useCardFindingsDay`. */
export function useCardFindingsAcross(day: IsoDate): { findings: readonly CardFinding[]; allowed: boolean } {
  const { cardFindings } = usePms();
  const { current } = usePmsScope();
  const reach = usePmsReachOrEmpty();
  const allowed = useCapability(CAPABILITIES.FIN_FINDINGS_READ);
  const codes = useMemo(
    () => (reach.length > 1 ? reach.map((b) => b.property.code) : current ? [current] : []),
    [reach, current],
  );
  const [findings, setFindings] = useState<readonly CardFinding[]>([]);

  useEffect(() => {
    if (!allowed || codes.length === 0) return;
    let cancelled = false;
    const load = async () => {
      const read = await Promise.all(codes.map((code) => cardFindings.day(code, day)));
      if (cancelled) return;
      setFindings(read.flatMap((r) => (r.ok ? r.value.findings : [])));
    };
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [cardFindings, codes, allowed, day]);

  return { findings, allowed };
}
